import React, { FC, ReactNode } from "react";
import { ReactComponent as Arrow } from "assets/images/iconsV2/arrow-up.svg";

import "./Badge.scss";

/**
 * Available colors for the Badge component.
 */
export type BadgeColor =
  | "brand"
  | "error"
  | "warning"
  | "success"
  | "indigo"
  | "secondary-orange"
  | "gray"
  | "purple"
  | "pink";

/**
 * Available sizes for the Badge component.
 */
export type BadgeSize = "sm" | "md" | "lg";

/**
 * Props for the Badge component.
 */
export type BadgeProps = {
  /**
   * The label displayed within the badge.
   */
  label: string | number | ReactNode;
  /**
   * The color of the badge, must be one of the defined BadgeColor.
   */
  color: BadgeColor;
  /**
   * The type of badge, which can be either "category-badge" or "status-badge".
   */
  type: "category-badge" | "status-badge";
  /**
   * Optional variant of the badge, controls the content within the badge. Works only for status-badge type
   */
  variant?: "text-only" | "dot" | "icon-leading" | "icon-trailing";
  /**
   * Optional size of the badge, controls the padding and font size.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Optional icon to be displayed if provided.
   */
  icon?: ReactNode;
};

/**
 * Badge component renders a customizable badge with different types, colors, and variants.
 *
 * @param {BadgeProps} props - The properties object for the Badge component.
 * @returns {JSX.Element} - A styled badge element.
 */

const Badge: FC<BadgeProps> = function (props) {
  const { type, color, label, size, variant, icon } = props;
  return (
    <div className={`custom-badge-v2 ${type} ${color} ${size}`}>
      {variant === "dot" && <span className={`dot ${color}`} />}
      {variant === "icon-leading" && (icon || <Arrow className={`icon ${color}`} />)}
      {label}
      {variant === "icon-trailing" && (icon || <Arrow className={`icon ${color}`} />)}
    </div>
  );
};

export default Badge;

Badge.defaultProps = {
  size: "sm",
  variant: "text-only",
  icon: "",
};
